import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getEmployee, getEmployees } from "../EmployeeApi";
import { getEmployeeSalaryStructure, listEmployeeSalaryStructures, calculateEmployeeSalary } from "../../Salary/SalaryApi";
import { fetchAttendance } from "../../attendance/attendanceApi";
import { fetchLeaveBalances, fetchLeaveRequests } from "../../leave/leaveApi";
import { fetchEmployeeSalaryRegister } from "../../reports/employeeSalaryRegisterApi";
import { getDirectReports, getEmployeeManager, getManagerChain } from "../hierarchyApi";
import { getEmployeeStatutory } from "../../Statutary/employeeStatutoryApi";
import { getEmployeeBankDetails } from "../EmployeeApi";
import { fetchOrgAuditLogs } from "../../../services/auditService";
import { getOnboarding, listOnboardings, listAuditLogs as listLifecycleAuditLogs } from "../../Lifecycle/lifecycleApi";
import { getEmployeeFuelBills } from "../../AllowancesBenefits/allowancesApi";
import { mapAttendanceRow } from "../../attendance/attendanceService";
import { fetchMyOrganisationSummary } from "../../attendance/attendanceApi";
import { isUuid, monthRangeISO, summarizeAttendanceRows } from "./employeeProfileUtils";

export function useResolveEmployeeId(routeParam) {
  const [state, setState] = useState({
    resolving: true,
    employeeId: null,
    resolveError: null,
  });

  useEffect(() => {
    let cancelled = false;
    const raw = String(routeParam || "").trim();
    if (!raw) {
      setState({ resolving: false, employeeId: null, resolveError: "missing" });
      return undefined;
    }

    (async () => {
      if (isUuid(raw)) {
        if (!cancelled) setState({ resolving: false, employeeId: raw, resolveError: null });
        return;
      }

      try {
        const data = await getEmployees({ search: raw, page: 1, page_size: 20, active_only: false });
        const items = Array.isArray(data) ? data : data?.items ?? [];
        const exact =
          items.find((e) => String(e.employee_code || "").toLowerCase() === raw.toLowerCase()) ||
          items.find((e) => String(e.employee_id) === raw);
        if (!cancelled) {
          setState({
            resolving: false,
            employeeId: exact?.employee_id ? String(exact.employee_id) : null,
            resolveError: exact ? null : "not_found",
          });
        }
      } catch {
        if (!cancelled) {
          setState({ resolving: false, employeeId: null, resolveError: "not_found" });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeParam]);

  return state;
}

export function useEmployeeCore(employeeId) {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(Boolean(employeeId));
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!employeeId) return null;
    setLoading(true);
    setError(null);
    try {
      const data = await getEmployee(employeeId);
      setEmployee(data);
      return data;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setError("not_found");
      else if (status === 403) setError("forbidden");
      else setError("load_failed");
      setEmployee(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { employee, loading, error, reload };
}

export function useOrgContext() {
  const [orgId, setOrgId] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const org = await fetchMyOrganisationSummary();
        if (!cancelled) setOrgId(org?.id || org?.organisation_id || "");
      } catch {
        if (!cancelled) setOrgId("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return orgId;
}

const emptySection = { loading: false, error: null, data: null, loaded: false };

export function useEmployeeProfileSections(employeeId, activeTab, orgId) {
  const cacheRef = useRef({});
  const [sections, setSections] = useState({});
  const [reloadNonce, setReloadNonce] = useState(0);

  const setSection = useCallback((key, patch) => {
    setSections((prev) => ({
      ...prev,
      [key]: { ...emptySection, ...prev[key], ...patch },
    }));
  }, []);

  const loadSection = useCallback(
    async (key, loader) => {
      if (!employeeId) return;
      const cached = cacheRef.current[key];
      if (cached) {
        setSection(key, { ...cached, loading: false, loaded: true });
        return;
      }
      setSection(key, { loading: true, error: null, loaded: false });
      try {
        const data = await loader();
        cacheRef.current[key] = { data, error: null };
        setSection(key, { loading: false, error: null, data, loaded: true });
      } catch (e) {
        const status = e?.response?.status;
        const error = status === 403 ? "forbidden" : "load_failed";
        cacheRef.current[key] = { data: null, error };
        setSection(key, { loading: false, error, data: null, loaded: true });
      }
    },
    [employeeId, setSection]
  );

  const reloadSection = useCallback(
    (key, loader) => {
      delete cacheRef.current[key];
      return loadSection(key, loader);
    },
    [loadSection]
  );

  useEffect(() => {
    if (!employeeId) return;

    const now = new Date();
    const { from_date, to_date } = monthRangeISO(now.getFullYear(), now.getMonth());

    loadSection("salary", async () => {
      const structure = await getEmployeeSalaryStructure(employeeId).catch(() => null);
      const historyRaw = await listEmployeeSalaryStructures().catch(() => []);
      const historyRows = Array.isArray(historyRaw) ? historyRaw : historyRaw?.items ?? [];
      const history = historyRows.filter((r) => String(r.employee_id) === String(employeeId));
      let calculation = null;
      try {
        calculation = await calculateEmployeeSalary(employeeId);
      } catch {
        calculation = null;
      }
      return { structure, calculation, history };
    });
    loadSection("attendanceSummary", async () => {
      const raw = await fetchAttendance({ employee_id: employeeId, from_date, to_date });
      const rows = (Array.isArray(raw) ? raw : raw?.items ?? []).map(mapAttendanceRow);
      return { rows, summary: summarizeAttendanceRows(rows), from_date, to_date };
    });
    if (orgId) {
      loadSection("leaveSummary", async () => {
        const balances = await fetchLeaveBalances({
          organisation_id: orgId,
          employee_id: employeeId,
          year: now.getFullYear(),
        });
        return { balances: Array.isArray(balances) ? balances : balances?.items ?? [] };
      });
    }
  }, [employeeId, loadSection, orgId, reloadNonce]);

  useEffect(() => {
    if (!employeeId) return;

    const now = new Date();
    const { from_date, to_date } = monthRangeISO(now.getFullYear(), now.getMonth());

    if (activeTab === "attendance") {
      loadSection("attendance", async () => {
        const raw = await fetchAttendance({ employee_id: employeeId, from_date, to_date });
        const rows = (Array.isArray(raw) ? raw : raw?.items ?? []).map(mapAttendanceRow);
        return { rows, summary: summarizeAttendanceRows(rows), from_date, to_date };
      });
    }

    if (activeTab === "leave" && orgId) {
      loadSection("leave", async () => {
        const [balances, requests] = await Promise.all([
          fetchLeaveBalances({
            organisation_id: orgId,
            employee_id: employeeId,
            year: now.getFullYear(),
          }),
          fetchLeaveRequests({ organisation_id: orgId, employee_id: employeeId }),
        ]);
        return {
          balances: Array.isArray(balances) ? balances : balances?.items ?? [],
          requests: Array.isArray(requests) ? requests : requests?.items ?? [],
        };
      });
    }

    if (activeTab === "payroll") {
      loadSection("payroll", async () => {
        const { data } = await fetchEmployeeSalaryRegister({
          search: employeeId,
          page: 1,
          page_size: 24,
          include_resigned: true,
        });
        const rows = data?.rows ?? data?.items ?? [];
        const mine = rows.filter(
          (r) =>
            String(r.employee_id) === String(employeeId) ||
            String(r.employee_code || "").toLowerCase() === String(employeeId).toLowerCase()
        );
        return { register: data, rows: mine.length ? mine : rows };
      });
    }

    if (activeTab === "organization") {
      loadSection("organization", async () => {
        const [manager, reports, chain] = await Promise.all([
          getEmployeeManager(employeeId).catch(() => null),
          getDirectReports(employeeId).catch(() => []),
          getManagerChain(employeeId).catch(() => []),
        ]);
        return {
          manager,
          directReports: Array.isArray(reports) ? reports : reports?.items ?? [],
          chain: Array.isArray(chain) ? chain : chain?.chain ?? [],
        };
      });
    }

    if (activeTab === "statutory") {
      loadSection("statutory", async () => getEmployeeStatutory(employeeId));
    }

    if (activeTab === "documents") {
      loadSection("documents", async () => {
        const [onboardingsRaw, fuel] = await Promise.all([
          listOnboardings({ page: 1, page_size: 100 }).catch(() => []),
          // All months: files employees submit as fuel bills (ESS or HR).
          getEmployeeFuelBills(employeeId).catch(() => null),
        ]);
        const all = Array.isArray(onboardingsRaw) ? onboardingsRaw : onboardingsRaw?.items ?? [];
        const onboardings = all.filter(
          (o) => String(o.employee_id || o.employee?.employee_id) === String(employeeId)
        );
        // Onboarding detail carries the individual documents uploaded via My Onboarding (ESS).
        const details = await Promise.all(
          onboardings.map((o) => getOnboarding(o.onboarding_id).catch(() => null))
        );
        const documents = details.flatMap((ob, idx) =>
          (ob?.documents || []).map((doc) => ({
            ...doc,
            onboarding_id: onboardings[idx].onboarding_id,
            onboarding_code: onboardings[idx].onboarding_code,
          }))
        );
        return { onboardings, documents, bills: fuel?.bills || [] };
      });
    }

    if (activeTab === "activity") {
      loadSection("activity", async () => {
        const [orgAudit, lifecycleAudit] = await Promise.all([
          fetchOrgAuditLogs({ limit: 200, q: employeeId }).catch(() => []),
          listLifecycleAuditLogs({ limit: 100 }).catch(() => []),
        ]);
        const orgRows = Array.isArray(orgAudit) ? orgAudit : orgAudit?.items ?? [];
        const lcRows = Array.isArray(lifecycleAudit) ? lifecycleAudit : lifecycleAudit?.items ?? [];
        const filteredLc = lcRows.filter((row) => {
          const blob = JSON.stringify(row || {}).toLowerCase();
          return blob.includes(String(employeeId).toLowerCase());
        });
        return { orgAudit: orgRows, lifecycleAudit: filteredLc };
      });
    }

    if (activeTab === "settings") {
      loadSection("settings", async () => {
        const bank = await getEmployeeBankDetails(employeeId).catch(() => null);
        return { bank };
      });
    }
  }, [activeTab, employeeId, loadSection, orgId, reloadNonce]); // tab-specific lazy loads

  const invalidateSection = useCallback((key) => {
    delete cacheRef.current[key];
    setReloadNonce((n) => n + 1);
  }, []);

  const getters = useMemo(
    () => ({
      get: (key) => sections[key] || emptySection,
      reload: reloadSection,
      invalidateSection,
    }),
    [sections, reloadSection, invalidateSection]
  );

  return getters;
}
